import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import axios from "axios";
import {
  TicketCheck, Plus, X, Paperclip, AlertCircle, FileText,
  ShieldCheck, UploadCloud, Eye, Send, Clock, Building2, User, CheckCircle2, EyeOff,
} from "lucide-react";
import formatApiError from "../../lib/apiError";
import { TICKET_STATUSES as STATUSES, STATUS_META, PRIORITY_META, ROLE_LABEL } from "../../lib/ticketStatus";
import { AuthContext } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const ax  = () => ({ withCredentials: true });

const ALLOWED_FILE_EXTS = [".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg", ".webp"];
const MAX_UPLOAD_MB = 10;

const IST = { timeZone: "Asia/Kolkata" };
function fmtDate(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", ...IST });
}
function fmtDateTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", ...IST })
    + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, ...IST });
}
function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, color: "#475569", bg: "#F1F5F9" };
  return <span className="status-tag" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}
function PriorityPill({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return <span className="priority-tag" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function UserTicketsPage() {
  const { user } = useContext(AuthContext);
  const isSupport = user?.role === "support_user" || user?.role === "admin";

  const [tickets, setTickets] = useState([]);
  const [filter, setFilter]   = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [showModal, setShowModal] = useState(false);
  const [viewing, setViewing] = useState(null); // ticket detail modal target

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/tickets`, ax());
      setTickets(data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load tickets."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Listen for "open-triage-modal" events fired by the notifications bell
  // when a new ticket arrives while the support user is logged in.
  useEffect(() => {
    const handler = async (e) => {
      const ticketId = e.detail?.ticket_id;
      if (!ticketId) return;
      try {
        const { data } = await axios.get(`${API}/api/tickets/${ticketId}`, ax());
        setViewing(data);
        fetchTickets();
      } catch (err) {
        // silent — modal just won't open
      }
    };
    window.addEventListener("open-triage-modal", handler);
    return () => window.removeEventListener("open-triage-modal", handler);
  }, [fetchTickets]);

  const counts = useMemo(() => {
    const c = { all: tickets.length, created: 0, in_progress: 0, closed: 0 };
    for (const t of tickets) if (c[t.status] != null) c[t.status]++;
    return c;
  }, [tickets]);

  const filtered = useMemo(() => {
    if (filter === "all") return tickets;
    return tickets.filter((t) => t.status === filter);
  }, [tickets, filter]);

  const [createSuccess, setCreateSuccess] = useState(false);

  const handleCreated = (newTicket) => {
    // Support users can always see their own tickets (even unpublished).
    // For other roles, newly raised tickets start unpublished so we show
    // a confirmation banner instead of inserting into the filtered list.
    if (newTicket?.published || isSupport) {
      setTickets((prev) => [newTicket, ...prev]);
    }
    setShowModal(false);
    setCreateSuccess(true);
  };

  const openTicket = async (t) => {
    // Refetch to ensure we have the latest responses
    try {
      const { data } = await axios.get(`${API}/api/tickets/${t.id}`, ax());
      setViewing(data);
    } catch {
      setViewing(t);
    }
  };

  const handleTicketUpdated = (updated) => {
    setViewing(updated);
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-sub">Raise tickets to MediMagic support — for yourself or on behalf of a faculty member.</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          data-testid="create-ticket-btn"
        >
          <Plus size={16} /> <span>Create Ticket</span>
        </button>
      </div>

      <div className="filter-tabs" role="tablist">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            className={`filter-tab${filter === s.key ? " active" : ""}`}
            onClick={() => setFilter(s.key)}
            data-testid={`filter-${s.key}`}
          >
            {s.label}
            <span className="filter-count">{counts[s.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {createSuccess && (
        <div className="settings-banner ok" data-testid="ticket-create-banner">
          <CheckCircle2 size={18} />
          <span>
            Ticket submitted — it&apos;s awaiting review by the Support team and will
            appear here once published.
          </span>
          <button
            className="settings-banner-close"
            onClick={() => setCreateSuccess(false)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <TicketCheck size={32} color="#43E0FF" />
              <p>No tickets yet. Click <strong>Create Ticket</strong> to raise one.</p>
            </div>
          ) : (
            <table className="data-table" data-testid="tickets-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>#</th>
                  <th>Title</th>
                  <th>On Behalf Of</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 90 }}>Priority</th>
                  <th style={{ width: 220 }}>Files</th>
                  <th style={{ width: 130 }}>Created</th>
                  <th style={{ width: 100, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} data-testid={`ticket-row-${t.id}`}>
                    <td className="mono">{t.ticket_no}</td>
                    <td>
                      <div className="ticket-title-cell">{t.title}</div>
                      {t.description && <div className="ticket-desc-cell">{t.description}</div>}
                    </td>
                    <td>
                      {t.on_behalf_of_professor_name
                        ? <span className="badge-info">{t.on_behalf_of_professor_name}</span>
                        : <span className="muted">—</span>}
                    </td>
                    <td><StatusPill status={t.status} /></td>
                    <td><PriorityPill priority={t.priority} /></td>
                    <td>
                      <div className="ticket-files-cell">
                        {t.attachment && (
                          <a
                            className="ticket-file-link"
                            href={`${API}${t.attachment.url}`}
                            target="_blank"
                            rel="noreferrer"
                            title={`${t.attachment.original_name} · ${fmtBytes(t.attachment.size)}`}
                          >
                            <Paperclip size={13} /> Document
                          </a>
                        )}
                        {t.permission_document && (
                          <a
                            className="ticket-file-link ticket-file-link-perm"
                            href={`${API}${t.permission_document.url}`}
                            target="_blank"
                            rel="noreferrer"
                            title={`${t.permission_document.original_name} · ${fmtBytes(t.permission_document.size)}`}
                          >
                            <ShieldCheck size={13} /> Permission
                          </a>
                        )}
                        {!t.attachment && !t.permission_document && <span className="muted">—</span>}
                      </div>
                    </td>
                    <td className="muted">{fmtDateTime(t.created_at)}</td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="btn-view"
                        onClick={() => openTicket(t)}
                        data-testid={`view-ticket-${t.id}`}
                        aria-label={`View ticket ${t.ticket_no}`}
                      >
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
       )
      }

      {showModal && (
        <CreateTicketModal
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
          isSupport={isSupport}
          showOnBehalf={!isSupport && user?.role !== "faculty"}
        />
      )}

      {viewing && (
        <TicketDetailModal
          ticket={viewing}
          onClose={() => setViewing(null)}
          onUpdated={handleTicketUpdated}
          isSupport={isSupport}
        />
      )}
    </div>
  );
}

// ── Create-Ticket Modal ──────────────────────────────────────────────────────
function CreateTicketModal({ onClose, onCreated, isSupport = false, showOnBehalf = false }) {
  const [title, setTitle]               = useState("");
  const [description, setDescription]   = useState("");
  const [priority, setPriority]         = useState("medium");
  const [attachment, setAttachment]     = useState(null);

  // College picker — only shown to support_user
  const [colleges, setColleges]         = useState([]);
  const [collegesLoading, setCollegesLoading] = useState(false);
  const [collegeId, setCollegeId]       = useState("");

  // On-behalf-of state (hidden for support_user)
  const [onBehalf, setOnBehalf]         = useState(false);
  const [professors, setProfessors]     = useState([]);
  const [profsLoading, setProfsLoading] = useState(false);
  const [professorId, setProfessorId]   = useState("");
  const [permissionDoc, setPermissionDoc] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr]       = useState("");
  const [fieldErr, setFieldErr]     = useState({});

  // Load colleges list for support_user
  useEffect(() => {
    if (!isSupport) return;
    let cancelled = false;
    (async () => {
      setCollegesLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/colleges`, ax());
        if (!cancelled) setColleges(data);
      } catch {
        // non-fatal — dropdown stays empty
      } finally {
        if (!cancelled) setCollegesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSupport]);

  // Lazy-load professors only when the user enables on-behalf-of.
  // Deps: only [onBehalf] — deliberately excludes profsLoading to avoid an
  // infinite loop when the API returns an empty list.
  useEffect(() => {
    if (!onBehalf || professors.length > 0) return;
    let cancelled = false;
    setProfsLoading(true);
    axios
      .get(`${API}/api/faculty`, ax())
      .then(({ data }) => { if (!cancelled) setProfessors(data); })
      .catch((e)       => { if (!cancelled) setFormErr(formatApiError(e, "Could not load professors.")); })
      .finally(()      => { if (!cancelled) setProfsLoading(false); });
    return () => { cancelled = true; };
  }, [onBehalf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on-behalf fields when the toggle is cleared
  useEffect(() => {
    if (!onBehalf) {
      setProfessorId("");
      setPermissionDoc(null);
    }
  }, [onBehalf]);

  const validateFile = (file, label) => {
    if (!file) return "";
    const lower = file.name.toLowerCase();
    const okExt = ALLOWED_FILE_EXTS.some((e) => lower.endsWith(e));
    if (!okExt) return `${label}: only ${ALLOWED_FILE_EXTS.join(", ")} are allowed.`;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) return `${label}: file exceeds ${MAX_UPLOAD_MB} MB.`;
    return "";
  };

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = "Subject is required.";
    if (!description.trim()) errs.description = "Message is required.";

    const attErr = validateFile(attachment, "Document");
    if (attErr) errs.attachment = attErr;

    if (!isSupport && onBehalf) {
      if (!professorId) errs.professorId = "Select the faculty member.";
      if (!permissionDoc) errs.permissionDoc = "Permission document is required.";
      else {
        const pErr = validateFile(permissionDoc, "Permission document");
        if (pErr) errs.permissionDoc = pErr;
      }
    }
    setFieldErr(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormErr("");
    if (!validate()) return;

    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("description", description.trim());
    fd.append("priority", priority);
    if (attachment) fd.append("attachment", attachment);

    if (isSupport) {
      // Support user: optionally tag a college; no on-behalf-of
      if (collegeId) fd.append("college_id_input", collegeId);
    } else if (onBehalf) {
      fd.append("on_behalf_of_professor_id", professorId);
      if (permissionDoc) fd.append("permission_document", permissionDoc);
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/tickets`, fd, {
        ...ax(),
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreated(data);
    } catch (e2) {
      setFormErr(formatApiError(e2, "Failed to create ticket."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box modal-box-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-ticket-modal"
      >
        <div className="modal-head">
          <h2 className="modal-title">Create Ticket</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {formErr && (
          <div className="form-error" data-testid="form-error">
            <AlertCircle size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            {formErr}
          </div>
        )}

        <div className="modal-body modal-body-scroll">
          {/* Ticket basics */}
          <div className="form-section">
            <p className="form-section-title">Ticket Details</p>

            {/* College picker — support user only */}
            {isSupport && (
              <div className="field">
                <label className="field-label">College <span className="field-hint">(optional)</span></label>
                <select
                  className="field-input"
                  value={collegeId}
                  onChange={(e) => setCollegeId(e.target.value)}
                  disabled={collegesLoading}
                  data-testid="ticket-college"
                >
                  <option value="">
                    {collegesLoading ? "Loading colleges…" : "— Not specific to a college —"}
                  </option>
                  {colleges.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="field">
              <label className="field-label">
                Subject <span className="required">*</span>
              </label>
              <input
                className={`field-input${fieldErr.title ? " field-input-error" : ""}`}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="A short summary of the issue"
                maxLength={200}
                autoFocus
                data-testid="ticket-title"
              />
              {fieldErr.title && <div className="field-error-text">{fieldErr.title}</div>}
            </div>

            <div className="field">
              <label className="field-label">
                Message <span className="required">*</span>
              </label>
              <textarea
                className={`field-input field-textarea${fieldErr.description ? " field-input-error" : ""}`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                placeholder="Describe the issue in detail…"
                maxLength={4000}
                data-testid="ticket-message"
              />
              {fieldErr.description && <div className="field-error-text">{fieldErr.description}</div>}
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Priority</label>
                <select
                  className="field-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  data-testid="ticket-priority"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="field">{/* spacer */}</div>
            </div>

            <div className="field">
              <label className="field-label">Upload Document <span className="field-hint">(optional)</span></label>
              <FileDropInput
                file={attachment}
                onChange={setAttachment}
                testid="ticket-attachment"
                error={fieldErr.attachment}
              />
              {fieldErr.attachment && <div className="field-error-text">{fieldErr.attachment}</div>}
            </div>
          </div>

          {/* On-behalf-of toggle — college/IT admin only */}
          {showOnBehalf && (
          <div className="form-section">
            <label className="onbehalf-toggle">
              <input
                type="checkbox"
                checked={onBehalf}
                onChange={(e) => setOnBehalf(e.target.checked)}
                data-testid="ticket-onbehalf-toggle"
              />
              <span className="onbehalf-toggle-text">
                <strong>Raise on behalf of another faculty</strong>
                <span className="onbehalf-toggle-sub">
                  Toggle this if you are creating a ticket for a professor who has authorised you to do so.
                </span>
              </span>
            </label>

            {onBehalf && (
              <div className="onbehalf-card" data-testid="ticket-onbehalf-section">
                <div className="field">
                  <label className="field-label">
                    Faculty member <span className="required">*</span>
                  </label>
                  <select
                    className={`field-input${fieldErr.professorId ? " field-input-error" : ""}`}
                    value={professorId}
                    onChange={(e) => setProfessorId(e.target.value)}
                    disabled={profsLoading || professors.length === 0}
                    data-testid="ticket-professor"
                  >
                    <option value="">
                      {profsLoading
                        ? "Loading professors…"
                        : professors.length === 0
                        ? "No faculty found — add from Users › Professors first"
                        : "Select a faculty member"}
                    </option>
                    {professors.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.subject_name ? ` · ${p.subject_name}` : ""}
                      </option>
                    ))}
                  </select>
                  {fieldErr.professorId && <div className="field-error-text">{fieldErr.professorId}</div>}
                </div>

                <div className="field">
                  <label className="field-label">
                    Permission Document <span className="required">*</span>
                  </label>
                  <p className="onbehalf-help">
                    A signed authorisation from the faculty member, allowing you to raise this ticket on their behalf.
                  </p>
                  <FileDropInput
                    file={permissionDoc}
                    onChange={setPermissionDoc}
                    testid="ticket-permission-doc"
                    error={fieldErr.permissionDoc}
                    variant="permission"
                  />
                  {fieldErr.permissionDoc && <div className="field-error-text">{fieldErr.permissionDoc}</div>}
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="ticket-submit"
          >
            {submitting ? "Creating…" : "Create Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ticket-Detail Modal (View + Reply) ──────────────────────────────────────
function TicketDetailModal({ ticket, onClose, onUpdated, isSupport = false }) {
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [publishSaving, setPublishSaving] = useState(false);

  const handleStatusChange = async (newStatus) => {
    if (!ticket || ticket.status === newStatus) return;
    setStatusSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/tickets/${ticket.id}/status`,
        { status: newStatus },
        ax(),
      );
      onUpdated(data);
    } catch (e) {
      alert(formatApiError(e, "Failed to update status"));
    } finally { setStatusSaving(false); }
  };

  const handlePublish = async (publishedValue) => {
    if (!ticket || ticket.published === publishedValue) return;
    setPublishSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/tickets/${ticket.id}/publish`,
        { published: publishedValue },
        ax(),
      );
      onUpdated(data);
    } catch (e) {
      alert(formatApiError(e, "Failed to update publish state"));
    } finally { setPublishSaving(false); }
  };

  const handleReply = async (e) => {
    e?.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    setPostErr("");
    try {
      const { data } = await axios.post(
        `${API}/api/tickets/${ticket.id}/responses`,
        { message: reply.trim() },
        ax(),
      );
      onUpdated(data);
      setReply("");
    } catch (e2) {
      setPostErr(formatApiError(e2, "Failed to post reply."));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box modal-box-lg ticket-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="ticket-detail-modal"
      >
        <div className="modal-head">
          <div>
            <p className="ticket-no">{ticket.ticket_no}</p>
            <h2 className="modal-title">{ticket.title}</h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="ticket-meta-row">
          <span className="ticket-meta-item">
            <Building2 size={13} /> {ticket.college_name || "—"}
          </span>
          <span className="ticket-meta-item">
            <User size={13} /> {ticket.raised_by_name}
            <span className="ticket-meta-sub">
              {" · "}{ROLE_LABEL[ticket.raised_by_role] || ticket.raised_by_role}
            </span>
          </span>
          <span className="ticket-meta-item">
            <Clock size={13} /> {fmtDateTime(ticket.created_at)}
          </span>
          <span className="ticket-meta-item">
            <StatusPill status={ticket.status} />
          </span>
          <span className="ticket-meta-item">
            <PriorityPill priority={ticket.priority} />
          </span>
        </div>

        <div className="modal-body modal-body-scroll ticket-body">
          {/* On-behalf-of badge */}
          {ticket.on_behalf_of_professor_name && (
            <div className="ticket-onbehalf-block">
              <ShieldCheck size={14} />
              <span>
                Raised on behalf of <strong>{ticket.on_behalf_of_professor_name}</strong>
              </span>
            </div>
          )}

          {/* Description */}
          <div className="ticket-desc-block">
            <p className="ticket-desc-label">Description</p>
            <p className="ticket-desc-text">
              {ticket.description || <span className="td-empty">No description provided.</span>}
            </p>
          </div>

          {/* Attached files */}
          {(ticket.attachment || ticket.permission_document) && (
            <div className="ticket-desc-block">
              <p className="ticket-desc-label">Files</p>
              <div className="ticket-files-cell">
                {ticket.attachment && (
                  <a
                    className="ticket-file-link"
                    href={`${API}${ticket.attachment.url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Paperclip size={13} /> {ticket.attachment.original_name}
                    <span className="muted" style={{ marginLeft: 6 }}>
                      ({fmtBytes(ticket.attachment.size)})
                    </span>
                  </a>
                )}
                {ticket.permission_document && (
                  <a
                    className="ticket-file-link ticket-file-link-perm"
                    href={`${API}${ticket.permission_document.url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ShieldCheck size={13} /> {ticket.permission_document.original_name}
                    <span className="muted" style={{ marginLeft: 6 }}>
                      ({fmtBytes(ticket.permission_document.size)})
                    </span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Triage controls — visible only to support/admin */}
          {isSupport && (
            <>
              <div className="ticket-status-block">
                <p className="ticket-desc-label">Status</p>
                <div className="status-segmented">
                  {STATUSES.filter((s) => s.key !== "all").map((s) => (
                    <button
                      key={s.key}
                      className={`status-seg-btn${ticket.status === s.key ? " active" : ""}`}
                      onClick={() => handleStatusChange(s.key)}
                      disabled={statusSaving}
                      style={ticket.status === s.key ? { background: s.color, color: "#fff", borderColor: s.color } : {}}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ticket-status-block">
                <p className="ticket-desc-label">
                  Publish this ticket?{" "}
                  {ticket.published === true && (
                    <span className="publish-tag publish-tag-on"><CheckCircle2 size={12} /> Published</span>
                  )}
                  {ticket.published === false && (
                    <span className="publish-tag publish-tag-off"><EyeOff size={12} /> Hidden</span>
                  )}
                  {ticket.published == null && (
                    <span className="publish-tag publish-tag-pending"><Clock size={12} /> Untriaged</span>
                  )}
                </p>
                <div className="publish-segmented">
                  <button
                    className={`publish-seg-btn publish-seg-yes${ticket.published === true ? " active" : ""}`}
                    onClick={() => handlePublish(true)}
                    disabled={publishSaving}
                  >
                    <CheckCircle2 size={14} /> Yes — publish
                  </button>
                  <button
                    className={`publish-seg-btn publish-seg-no${ticket.published === false ? " active" : ""}`}
                    onClick={() => handlePublish(false)}
                    disabled={publishSaving}
                  >
                    <EyeOff size={14} /> No — keep hidden
                  </button>
                </div>
                <p className="ticket-publish-hint">
                  Unpublished tickets are visible only to Super Admin and Support User.
                </p>
              </div>
            </>
          )}

          {/* Conversation thread */}
          <div className="ticket-thread">
            <p className="ticket-desc-label">
              Conversation ({ticket.responses?.length || 0})
            </p>
            {ticket.responses?.length === 0 ? (
              <p className="td-empty" style={{ margin: "8px 0" }}>No replies yet.</p>
            ) : (
              ticket.responses.map((r) => {
                const isSupport = r.author_role === "admin";
                return (
                  <div
                    key={r.id}
                    className={`reply ${isSupport ? "reply-admin" : "reply-user"}`}
                    data-testid={`reply-${r.id}`}
                  >
                    <div className="reply-head">
                      <span className="reply-author">{r.author_name}</span>
                      <span className="reply-role">{ROLE_LABEL[r.author_role] || r.author_role}</span>
                      <span className="reply-time">{fmtDateTime(r.created_at)}</span>
                    </div>
                    <p className="reply-msg">{r.message}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Reply box */}
        <form className="reply-form" onSubmit={handleReply}>
          {postErr && (
            <div className="form-error" data-testid="reply-error" style={{ width: "100%" }}>
              <AlertCircle size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              {postErr}
            </div>
          )}
          <textarea
            className="field-input field-textarea reply-input"
            placeholder="Write a reply…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={2}
            maxLength={4000}
            data-testid="reply-input"
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={posting || !reply.trim()}
            data-testid="send-reply-btn"
          >
            {posting ? <span className="btn-spinner" /> : (<><Send size={14} /> Reply</>)}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── File picker (drop-zone style) ───────────────────────────────────────────
function FileDropInput({ file, onChange, testid, error, variant }) {
  const inputId = `file-${testid}`;
  const isPerm = variant === "permission";
  return (
    <div>
      {!file ? (
        <label
          htmlFor={inputId}
          className={`file-drop${error ? " file-drop-error" : ""}${isPerm ? " file-drop-perm" : ""}`}
        >
          {isPerm ? <ShieldCheck size={22} /> : <UploadCloud size={22} />}
          <span className="file-drop-text">
            <strong>Click to upload</strong> or drag a file here
          </span>
          <span className="file-drop-hint">{ALLOWED_FILE_EXTS.join(", ")} · max {MAX_UPLOAD_MB} MB</span>
          <input
            id={inputId}
            type="file"
            accept={ALLOWED_FILE_EXTS.join(",")}
            onChange={(e) => onChange(e.target.files?.[0] || null)}
            data-testid={testid}
            style={{ display: "none" }}
          />
        </label>
      ) : (
        <div className="file-chip" data-testid={`${testid}-chip`}>
          <FileText size={16} />
          <div className="file-chip-meta">
            <span className="file-chip-name">{file.name}</span>
            <span className="file-chip-size">{fmtBytes(file.size)}</span>
          </div>
          <button
            type="button"
            className="file-chip-remove"
            onClick={() => onChange(null)}
            aria-label="Remove file"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
